import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { AgentRecord } from "../AgentRecord";

interface UseAgentOptions {
  /** Caller's org id; the backend requires it on every read. */
  orgId?: string;
}

/**
 * Fetch a single Agent by id.
 *
 * Query is disabled until both ``agentId`` and ``orgId`` are defined so
 * callers can mount the hook before route params resolve without firing a
 * doomed request.
 */
export function useAgent(
  agentId: string | undefined,
  options?: UseAgentOptions,
): UseQueryResult<AgentRecord, Error> {
  const { agentService } = useDomainServices();

  return useQuery<AgentRecord, Error>({
    queryKey: ["agent", agentId],
    queryFn: ({ signal }) => {
      if (agentId === undefined) {
        return Promise.reject(new Error("agentId is required"));
      }
      return agentService.getAgent(agentId, {
        orgId: options?.orgId,
        abortSignal: signal,
      });
    },
    enabled: agentId !== undefined && options?.orgId !== undefined,
    staleTime: 30_000,
  });
}
